import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { FOOTER_LINKS, SITE } from "@/config/site";
import { track, whatsappLink } from "@/lib/site";
import { Container, WhatsAppIcon } from "./Primitives";

const SocialIcon = ({ id }) => {
  const paths = {
    facebook: "M13.5 21v-7.5h2.5l.4-3H13.5V8.6c0-.87.25-1.46 1.5-1.46h1.6V4.46A21 21 0 0 0 14.27 4.3c-2.3 0-3.88 1.4-3.88 3.98v2.22H7.8v3h2.6V21h3.1Z",
    instagram: "M12 7.3a4.7 4.7 0 1 0 0 9.4 4.7 4.7 0 0 0 0-9.4Zm0 7.75a3.05 3.05 0 1 1 0-6.1 3.05 3.05 0 0 1 0 6.1Zm4.9-8.98a1.1 1.1 0 1 1-2.2 0 1.1 1.1 0 0 1 2.2 0ZM20 8.2c-.06-1.36-.37-2.57-1.37-3.56-.99-1-2.2-1.3-3.56-1.37-1.4-.08-5.6-.08-7.01 0-1.36.06-2.56.37-3.56 1.36-1 1-1.3 2.2-1.37 3.56-.08 1.41-.08 5.6 0 7.01.06 1.36.37 2.57 1.37 3.56 1 1 2.2 1.3 3.56 1.37 1.41.08 5.6.08 7.01 0 1.36-.06 2.57-.37 3.56-1.37 1-.99 1.3-2.2 1.37-3.56.08-1.41.08-5.6 0-7Zm-1.82 8.52a2.87 2.87 0 0 1-1.62 1.62c-1.12.44-3.78.34-5.02.34s-3.9.1-5.02-.34a2.87 2.87 0 0 1-1.61-1.62c-.45-1.12-.35-3.78-.35-5.02s-.1-3.9.35-5.02a2.87 2.87 0 0 1 1.61-1.61c1.13-.45 3.79-.35 5.02-.35s3.9-.1 5.02.35c.73.29 1.33.88 1.62 1.61.44 1.13.34 3.79.34 5.02s.1 3.9-.34 5.02Z",
    tiktok: "M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.6c.27 0 .53.04.77.12V9.77a5.68 5.68 0 0 0-.77-.05 5.68 5.68 0 1 0 5.68 5.68V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.24-1.48Z",
  };
  return <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true"><path d={paths[id]} /></svg>;
};

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#161616] pb-24 text-white md:pb-0" data-testid="site-footer">
      <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-4">
          <img src="/images/khozaland-logo-white-sm.png" alt="KhozaLand logo" width="356" height="259" loading="lazy" className="h-16 w-auto" />
          <p className="mt-6 font-display text-2xl tracking-tight">{SITE.motto}</p>
          <p className="mt-3 max-w-sm text-sm leading-6 text-white/65">
            {SITE.legalName} — a {SITE.ownership} printing and branding business in {SITE.locationLabel}, {SITE.region}. Custom clothing, corporate gifts, personalised gifts and Xhosa cultural products.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-white/80">
            <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-white/50" aria-hidden="true" /> {SITE.locationLabel}, {SITE.country}</li>
            <li><a href={`tel:${SITE.phoneE164}`} onClick={() => track("phone_click", { label: "footer" })} className="flex items-center gap-3 hover:text-white" data-testid="footer-phone-link"><Phone className="h-4 w-4 text-white/50" aria-hidden="true" /> {SITE.phoneDisplay}</a></li>
            <li><a href={whatsappLink("general")} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { label: "footer" })} className="flex items-center gap-3 hover:text-white" data-testid="footer-whatsapp-link"><WhatsAppIcon className="h-4 w-4 text-white/50" /> WhatsApp {SITE.phoneDisplay}</a></li>
            <li><a href={`mailto:${SITE.email}`} onClick={() => track("email_click", { label: "footer" })} className="flex items-center gap-3 hover:text-white" data-testid="footer-email-link"><Mail className="h-4 w-4 text-white/50" aria-hidden="true" /> {SITE.email}</a></li>
          </ul>
          <div className="mt-6 flex gap-2">
            {SITE.social.map((s) => (
              <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`KhozaLand on ${s.label} (opens in a new tab)`} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/80 transition-colors hover:border-white/40 hover:text-white" data-testid={`footer-social-${s.id}-link`}>
                <SocialIcon id={s.id} />
              </a>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-8 lg:pl-10">
          {FOOTER_LINKS.map((col) => (
            <div key={col.title}>
              <p className="kl-eyebrow text-white/50">{col.title}</p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}><Link to={l.to} className="text-sm text-white/80 transition-colors hover:text-white" data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}>{l.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>
      <div className="border-t border-white/10">
        <Container className="flex flex-col gap-3 py-6 text-xs text-white/50 sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} {SITE.legalName}. Reg. No. {SITE.registrationNumber}. {SITE.tagline}</p>
          <div className="flex gap-5">
            <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms & Conditions</Link>
          </div>
        </Container>
      </div>
    </footer>
  );
}
